/**
 * Which CATS records are payable — the S/4 half of "approved time" for Time
 * Evaluation and the ADP payroll file.
 *
 * On the S/4 build there are TWO approval signals and payroll must honour both:
 *
 *   1. CATS status 30 (Approved). Time approved in SAP itself — CAT2,
 *      CATS_APPR_LITE, or a data entry profile with no approval step — only
 *      ever shows up this way.
 *   2. The app's approval overlay (ZTM12_TS_APPR, surfaced as approved
 *      submissions). The in-app approve action writes the overlay; it does NOT
 *      move the CATS record to 30 (there is no approve BAPI), so app-approved
 *      time sits at CATS status 10/20 inside an approved week.
 *
 * And some statuses are NEVER paid, whatever the overlay says (standard CATS
 * domain CATSSTATUS, confirmed from DD07T on Codan RD1):
 *
 *   40  Approval rejected
 *   50  Changed after approval  — the superseded original; its replacement
 *                                 record carries the hours
 *   60  Cancelled
 *
 * Paying a 50 alongside its replacement pays the hours twice; paying a 60 pays
 * hours someone cancelled. Both are the kind of error that looks fine in a
 * total and is only found by the worker.
 *
 * PURE. No I/O.
 */

export const CATS_PAYABLE_STATUS = '30'
export const CATS_NEVER_PAYABLE: ReadonlySet<string> = new Set(['40', '50', '60'])
const CATS_PENDING: ReadonlySet<string> = new Set(['10', '20'])

/** SAP PERNR is NUMC 8. The TimeSheet entity returns it unpadded ('8'); Personnel pads it. */
export function normalizePernr(pernr: string | null | undefined): string {
  const v = (pernr ?? '').trim()
  return /^\d+$/.test(v) && v.length < 8 ? v.padStart(8, '0') : v
}

export interface CatsRowLike {
  Pernr: string
  WorkDate: string
  CatsStatus?: string
  RecordedHours: number | string
}

export interface ApprovedWeek {
  /** Personnel number, any padding. */
  pernr: string
  /** ISO date the approved week starts on (the aligned week the overlay stores). */
  weekStart: string
}

function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1))
  dt.setUTCDate(dt.getUTCDate() + days)
  return dt.toISOString().slice(0, 10)
}

/** pernr -> list of [start, end) windows of app-approved weeks. */
export function indexApprovedWeeks(weeks: readonly ApprovedWeek[]): Map<string, Array<[string, string]>> {
  const out = new Map<string, Array<[string, string]>>()
  for (const w of weeks) {
    if (!w.weekStart) continue
    const key = normalizePernr(w.pernr)
    const list = out.get(key) ?? []
    list.push([w.weekStart, addDaysIso(w.weekStart, 7)])
    out.set(key, list)
  }
  return out
}

export type CatsPayability = 'payable_cats_approved' | 'payable_app_approved' | 'not_approved' | 'never_payable' | 'zero_hours'

/** Why a row is (or is not) paid. Exposed so a caller can count the exclusions. */
export function classifyCatsRow(
  row: CatsRowLike,
  approvedWeeks: Map<string, Array<[string, string]>>,
): CatsPayability {
  const status = (row.CatsStatus ?? '').trim()
  if (CATS_NEVER_PAYABLE.has(status)) return 'never_payable'
  if (Number(row.RecordedHours ?? 0) === 0) return 'zero_hours'
  if (status === CATS_PAYABLE_STATUS) return 'payable_cats_approved'
  if (CATS_PENDING.has(status)) {
    const windows = approvedWeeks.get(normalizePernr(row.Pernr)) ?? []
    const d = row.WorkDate
    if (windows.some(([start, end]) => d >= start && d < end)) return 'payable_app_approved'
  }
  return 'not_approved'
}

export interface PayableSelection<T> {
  rows: T[]
  /** Counts per classification, for diagnostics. */
  counts: Record<CatsPayability, number>
}

/** Filter period rows down to the payable ones, counting every exclusion. */
export function selectPayableCatsRows<T extends CatsRowLike>(
  rows: readonly T[],
  approvedWeeks: readonly ApprovedWeek[],
): PayableSelection<T> {
  const idx = indexApprovedWeeks(approvedWeeks)
  const counts: Record<CatsPayability, number> = {
    payable_cats_approved: 0,
    payable_app_approved: 0,
    not_approved: 0,
    never_payable: 0,
    zero_hours: 0,
  }
  const out: T[] = []
  for (const r of rows) {
    const c = classifyCatsRow(r, idx)
    counts[c] += 1
    if (c === 'payable_cats_approved' || c === 'payable_app_approved') out.push(r)
  }
  return { rows: out, counts }
}
